import { useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff, Lock, ShieldCheck } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { ProfileShell } from "../../components/profile/ProfileShell";

// IMPORTANT: defined OUTSIDE the parent component so React keeps the same
// component identity across renders — otherwise the <input> remounts on
// every keystroke and loses focus.
function PasswordField({ label, field, value, onChange, shown, onToggle }) {
  return (
    <label className="block">
      <span className="text-xs text-white/55 uppercase tracking-wider">{label}</span>
      <div className="relative mt-1">
        <input
          data-testid={`pwd-${field}`}
          type={shown ? "text" : "password"}
          value={value}
          onChange={onChange}
          className="w-full h-11 rounded-lg bg-[#101824] border border-white/10 px-3 pr-11 text-white placeholder:text-white/30 focus:border-[#19a55c] focus:outline-none focus:ring-2 focus:ring-[#19a55c]/25"
          placeholder="••••••"
          autoComplete={field === "current_password" ? "current-password" : "new-password"}
        />
        <button
          type="button"
          onClick={onToggle}
          className="absolute inset-y-0 right-0 w-10 flex items-center justify-center text-white/50 hover:text-white"
          aria-label="toggle"
        >
          {shown ? <EyeOff size={17} /> : <Eye size={17} />}
        </button>
      </div>
    </label>
  );
}

export default function LoginSecurityPage() {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [show, setShow] = useState({ cur: false, ne: false, cf: false });
  const [saving, setSaving] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm_password) {
      toast.error("New passwords don't match");
      return;
    }
    if (form.new_password.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    setSaving(true);
    try {
      await api.put("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success("Password updated");
      setForm({ current_password: "", new_password: "", confirm_password: "" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to update password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ProfileShell title="Password change">
      <div className="py-3 pb-10 space-y-5">
        {/* Hero header */}
        <div
          className="relative overflow-hidden rounded-2xl border border-white/10 p-5 sm:p-6"
          style={{
            background:
              "radial-gradient(900px 280px at 0% 0%, rgba(25,165,92,0.18), transparent 60%)," +
              "radial-gradient(700px 240px at 100% 100%, rgba(56,189,248,0.10), transparent 60%)," +
              "linear-gradient(180deg,#0d1422 0%,#0a1018 100%)",
          }}
        >
          <div aria-hidden className="pointer-events-none absolute inset-0 opacity-[0.05]" style={{
            backgroundImage: "linear-gradient(rgba(255,255,255,.7) 1px, transparent 1px),linear-gradient(90deg, rgba(255,255,255,.7) 1px, transparent 1px)",
            backgroundSize: "40px 40px",
            maskImage: "radial-gradient(ellipse at center, black 30%, transparent 75%)",
          }} />
          <div className="relative flex items-center gap-4">
            <div className="h-14 w-14 rounded-2xl grid place-items-center"
              style={{ background: "linear-gradient(135deg,#34d399 0%,#10b981 60%,#059669 100%)", boxShadow: "0 12px 30px -8px rgba(16,185,129,0.55)" }}>
              <ShieldCheck size={26} className="text-[#06140b]" strokeWidth={2.4} />
            </div>
            <div className="flex-1">
              <div className="text-[10.5px] uppercase tracking-[2px] font-bold text-emerald-300/85">Account Security</div>
              <div className="text-xl sm:text-2xl font-black tracking-tight mt-0.5">Lock down your account</div>
              <div className="text-[12px] text-white/55 mt-0.5">Keep your account safe with a strong, unique password.</div>
            </div>
          </div>
        </div>

        {/* Change password card */}
        <form onSubmit={onSubmit} data-testid="login-security-form">
          <div className="rounded-2xl bg-[#0d1422]/85 border border-white/[0.07] p-5 sm:p-6 space-y-4 backdrop-blur-xl">
            <div className="flex items-center gap-3 pb-3 border-b border-white/[0.06]">
              <div className="h-9 w-9 rounded-lg bg-emerald-500/12 border border-emerald-500/30 grid place-items-center text-emerald-300">
                <Lock size={16} />
              </div>
              <div className="flex-1">
                <div className="text-[15px] font-bold tracking-tight">Change password</div>
                <div className="text-[11.5px] text-white/45">Use at least 6 characters — mix letters, numbers and symbols.</div>
              </div>
            </div>
            <PasswordField label="Current password" field="current_password"
              value={form.current_password}
              onChange={(e) => setForm((f) => ({ ...f, current_password: e.target.value }))}
              shown={show.cur} onToggle={() => setShow((s) => ({ ...s, cur: !s.cur }))} />
            <PasswordField label="New password" field="new_password"
              value={form.new_password}
              onChange={(e) => setForm((f) => ({ ...f, new_password: e.target.value }))}
              shown={show.ne} onToggle={() => setShow((s) => ({ ...s, ne: !s.ne }))} />
            <PasswordField label="Confirm new password" field="confirm_password"
              value={form.confirm_password}
              onChange={(e) => setForm((f) => ({ ...f, confirm_password: e.target.value }))}
              shown={show.cf} onToggle={() => setShow((s) => ({ ...s, cf: !s.cf }))} />
            <button
              type="submit"
              data-testid="change-password-btn"
              disabled={saving}
              className="group relative w-full h-12 rounded-xl text-[#06140b] font-extrabold tracking-wide overflow-hidden disabled:opacity-60 transition-all"
              style={{
                background: "linear-gradient(135deg,#34d399 0%,#10b981 50%,#059669 100%)",
                boxShadow: "0 12px 30px -10px rgba(16,185,129,0.6), inset 0 1px 0 rgba(255,255,255,0.25)",
              }}
            >
              <span aria-hidden className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent translate-x-[-150%] group-hover:translate-x-[150%] transition-transform duration-700" />
              <span className="relative">{saving ? "Updating…" : "Update password"}</span>
            </button>
          </div>
        </form>
      </div>
    </ProfileShell>
  );
}
